import React, { useEffect, useState } from 'react';
import { UserOutlined, BookOutlined, VideoCameraOutlined, HighlightOutlined, NumberOutlined, UsergroupAddOutlined, LineChartOutlined } from '@ant-design/icons';
import { Layout, Menu, theme } from 'antd';
import { ReactComponent as WaveIcon } from 'resources/images/cc.svg';
import { LogoWithText } from 'components/Logo';
import ConversationsPage from './ConversationsPage';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AccountPage from './AccountPage';
import QuizRoute from './QuizPage';
import AuthService from 'services/AuthService';
import CurateQuestionsPage from './CurateQuestionsPage';
import CuratedStats from './CuratedStats';
import ManageAccounts from './ManageAccounts';
import StatsPage from './StatsPage';
import StudentStatsPage from './StudentStatsPage';
import { ReadingAssessmentResponsesPage } from './ReadingAssessmentResponse';
import KnowledgeBasePage from './KnowledgeBasePage/index';
import DocumentPage from './KnowledgeBasePage/DocumentPage';
import EditBookPage from './KnowledgeBasePage/EditBookPage';
import type { Location as ReactRouterLocation } from "@remix-run/router";
import CurateMetadataPage from './CurateMetadataPage';

const { Sider } = Layout;


const computeItems = () => {
  let items: any[] = []
  if (AuthService.isCurator() || AuthService.isRoleAuthenticated()) {
    items.push({
      key: "1",
      icon: React.createElement(VideoCameraOutlined),
      label: "Your Quiz",
      component: QuizRoute
    })
    items.push({
      key: "2",
      icon: <div><WaveIcon /></div>,
      label: "Your Conversations",
      component: ConversationsPage
    })
  }

  if (AuthService.isCurator()) {
    items.push({
      key: "5",
      icon: React.createElement(HighlightOutlined),
      label: "Curate Questions",
      component: CurateQuestionsPage
    })
    items.push({
      key: "11",
      icon: React.createElement(HighlightOutlined),
      label: "Curate",
      component: CurateMetadataPage
    })
    items.push({
      key: "6",
      icon: React.createElement(NumberOutlined),
      label: "Approved Stats",
      component: CuratedStats
    })
  }

  if (AuthService.isCurator() || AuthService.isRoleTeacher()) {
    items.push({
      key: "7",
      icon: React.createElement(UsergroupAddOutlined),
      label: "Assign Lessons",
      component: ManageAccounts
    })
    items.push({
      key: "8",
      icon: React.createElement(LineChartOutlined),
      label: "School Performance",
      component: StatsPage
    })
    items.push({
      key: "9",
      icon: React.createElement(LineChartOutlined),
      label: "Class & Students",
      component: StudentStatsPage,
      additionalPrefixes: ["/assessment"]
    })
    items.push({
      key: "10",
      icon: React.createElement(BookOutlined),
      label: "Knowledge Base",
      component: KnowledgeBasePage,
      additionalPrefixes: ["/document", "/knowledge_base"]
    })
  }

  items.push({
    key: "3",
    icon: React.createElement(UserOutlined),
    label: "Your Account",
    component: AccountPage
  })
  return items
}

const SELECTED_MENU_SEARCH_PARAM = "menu"

const getSelectedMenu = (items: any[], searchParams: URLSearchParams, location: ReactRouterLocation) => {
  console.log("location", location)
  if (searchParams.get(SELECTED_MENU_SEARCH_PARAM)) {
    return searchParams.get(SELECTED_MENU_SEARCH_PARAM)
  }

  let matchedItemKey = null
  let anyAdditionalPrefixMatch = items.some(item => {
    return item.additionalPrefixes?.some(prefix => {
      matchedItemKey = item.key
      return location.pathname.startsWith(prefix)
    })
  })

  if (anyAdditionalPrefixMatch) {
    return matchedItemKey
  }

  return items[0].key
}

const DashboardPage: React.FC = () => {
  const [items, setItems] = useState(() => computeItems())
  const [collapsed, setCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  let location = useLocation();

  let navigate = useNavigate();

  const selectedKey = getSelectedMenu(items, searchParams, location);
  let selectedItem = items.filter(i => i.key === selectedKey)[0]

  console.log("Rendering dashboard..", items, selectedItem, AuthService.getRole())
  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: "white" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
      >
        <div className='my-5' style={{ paddingLeft: 24, paddingRight: 16 }}>
          <LogoWithText showText={!collapsed} />
        </div>
        <Menu
          className='my-6'
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          onSelect={(selection) => {
            console.log("Selection", selection)
            searchParams.set(SELECTED_MENU_SEARCH_PARAM, selection.key)
            navigate({
              pathname: "/",
              search: searchParams.toString()
            })
          }}
          items={items.map(({ additionalPrefixes, ...rest }) => rest)} // Exclude `additionalPrefixes`
        />
      </Sider>
      <Layout className='has-background-white	'>
        <Routes>
          <Route path="/assessment/:assessment_id" element={<ReadingAssessmentResponsesPage />} />
          <Route path="/document/:document_id" element={<DocumentPage />} />
          <Route path="/knowledge_base/:document_id" element={<EditBookPage />} />
          <Route index path="*" element={React.createElement(selectedItem.component)} />
        </Routes>
      </Layout>
    </Layout>
  );
};

export default DashboardPage;